import React from "react";
import moment from "moment";
import dayjs from "dayjs";
import { IconButton } from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import Functions from "../../Helpers/Functions";
import FormDate from "../../Components/FormFields/FormDate";
import TimeFields from "../../Components/Fields/TimeFields";
import DurationFields from "../../Components/Fields/DurationFields";
import FormInput from "../../Components/Ui/FormInput";
import ButtonField from "../../Components/Ui/ButtonField";
import { BrandContent, useTailwindBrand } from "../../Components/shared";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { getActivePrestationBrandConfig } from "../../config/environment.config";
import { InternalOverlap, describeInternalOverlap } from "./overlapMessages";
import SessionCalendar from "./SessionCalendar";

/**
 * Nombre maximum de séances pour un cours mensuel (aligné backend,
 * PunctualCourse::MAX_SESSIONS_MONTH). Porté de 8 à 20 le 29 octobre 2026 avec
 * le calendrier du mois : plusieurs séances par semaine chez un même élève.
 */
export const MAX_SESSIONS_MONTH = 20;
/**
 * Nombre minimum de séances d'un cours du mois (« Plusieurs cours ») : une
 * seule séance est un cours ponctuel. Contrôle côté front, au moment où le
 * prof choisit ce type de déclaration. Les anciens cours du mois à une seule
 * séance (reprise P1) restent modifiables : l'appelant passe alors `minSessions: 1`.
 */
export const MIN_SESSIONS_MONTH = 2;
/** P1 – Durée maximale d'une séance en heures (aligné backend : 240 min). */
export const MAX_SESSION_DURATION_HOURS = 4;

/**
 * Une ligne de séance saisie dans le flux de déclaration mensuelle.
 * Valeurs en ISO strings : l'objet est stocké dans le state Redux persisté.
 */
export interface SessionRow {
    key: string;
    /** Jour de la séance (ISO, seule la partie date compte). */
    date: string | null;
    /** Heure de début (ISO datetime, seule l'heure compte). */
    startTime: string | null;
    /** Durée en heures (pas de 0,25, max 4). */
    duration: number;
    /**
     * Séance déjà passée au moment de l'ouverture de l'édition :
     * affichée en lecture seule, ni modifiable ni supprimable.
     */
    locked?: boolean;
}

let sessionRowCounter = 0;
export const createEmptySessionRow = (): SessionRow => ({
    key: `session-${Date.now()}-${sessionRowCounter++}`,
    date: null,
    startTime: null,
    duration: 1.5,
});

/** Combine jour + heure de début en un moment complet, ou null si incomplet. */
export const sessionStart = (row: SessionRow) => {
    if (!row.date || !row.startTime) {
        return null;
    }
    const time = moment(row.startTime);
    return moment(row.date)
        .set("hour", time.hours())
        .set("minutes", time.minutes())
        .set("seconds", 0);
};

/** Fin de séance (début + durée), ou null si la ligne est incomplète. */
export const sessionEnd = (row: SessionRow) => {
    const start = sessionStart(row);
    return start ? start.clone().add(row.duration, "hours") : null;
};

/** Durée totale de toutes les lignes, en heures. */
export const sessionsTotalHours = (rows: SessionRow[]) =>
    rows.reduce((total, row) => total + (row.duration || 0), 0);

/** Bornes semi-ouvertes : un cours qui finit à 11h et un autre qui commence à 11h ne se chevauchent pas. */
export const sessionRangesOverlap = (aStart: moment.Moment, aEnd: moment.Moment, bStart: moment.Moment, bEnd: moment.Moment): boolean =>
    aStart.isBefore(bEnd) && bStart.isBefore(aEnd);

/** « 30/09/2026 de 10:00 à 11:00 » */
export const formatSessionSlot = (start: moment.Moment, end: moment.Moment): string =>
    `${start.format("DD/MM/YYYY")} de ${start.format("HH:mm")} à ${end.format("HH:mm")}`;

/**
 * Deux séances d'une même déclaration qui se chevauchent. Deux séances
 * verrouillées (déjà passées) ne sont pas comparées : elles ne sont plus
 * corrigeables. Les lignes incomplètes sont ignorées.
 */
export const findInternalOverlap = (rows: SessionRow[]): InternalOverlap | null => {
    for (let i = 0; i < rows.length; i++) {
        const aStart = sessionStart(rows[i]);
        const aEnd = sessionEnd(rows[i]);
        if (!aStart || !aEnd) continue;
        for (let j = i + 1; j < rows.length; j++) {
            if (rows[i].locked && rows[j].locked) continue;
            const bStart = sessionStart(rows[j]);
            const bEnd = sessionEnd(rows[j]);
            if (!bStart || !bEnd) continue;
            if (sessionRangesOverlap(aStart, aEnd, bStart, bEnd)) {
                return { first: i + 1, second: j + 1, firstSlot: formatSessionSlot(aStart, aEnd), secondSlot: formatSessionSlot(bStart, bEnd) };
            }
        }
    }
    return null;
};

/**
 * Même séance le mois suivant, en gardant le jour de la semaine et son rang
 * dans le mois : le 1er mardi d'octobre devient le 1er mardi de novembre, le
 * 4e jeudi devient le 4e jeudi. L'heure est conservée. Retourne null quand le
 * rang n'existe pas dans le mois suivant (5e jeudi d'octobre 2026, novembre
 * n'en compte que quatre) : au prof de choisir la date.
 */
export const shiftToNextMonthSameWeekday = (start: moment.Moment): moment.Moment | null => {
    const rank = Math.ceil(start.date() / 7);
    const nextMonth = start.clone().add(1, "month").startOf("month");
    // premier jour du mois suivant portant le même jour de semaine
    const firstSameWeekday = nextMonth.clone().add((start.day() - nextMonth.day() + 7) % 7, "day");
    const target = firstSameWeekday.add(rank - 1, "week");
    if (!target.isSame(nextMonth, "month")) {
        return null;
    }
    return target.set({ hour: start.hours(), minute: start.minutes(), second: 0, millisecond: 0 });
};

/**
 * Date d'une séance de brouillon re-déclaré (`monthOffset` mois plus tard,
 * règle du jour de semaine appliquée mois par mois), ou null si une étape n'a
 * pas d'équivalent.
 */
export const shiftDraftSessionDate = (date: string, monthOffset: number): moment.Moment | null => {
    let current: moment.Moment | null = moment(date);
    for (let i = 0; i < monthOffset && current; i++) {
        current = shiftToNextMonthSameWeekday(current);
    }
    return current;
};

/**
 * Convertit les séances renvoyées par l'API ([{id, date, durationMinutes}])
 * en lignes éditables. `monthOffset` décale les dates d'un brouillon re-déclaré
 * (+1 mois) en gardant le jour de la semaine (shiftToNextMonthSameWeekday) :
 * un prof qui donne ses cours le mardi retrouve ses mardis. Une séance sans
 * équivalent garde son heure et sa durée, mais sa date reste à renseigner.
 */
export const apiSessionsToRows = (
    apiSessions: Array<{ id?: number; date: string; durationMinutes: number }> | null | undefined,
    monthOffset = 0
): SessionRow[] => {
    if (!Array.isArray(apiSessions) || apiSessions.length === 0) {
        return [createEmptySessionRow()];
    }
    // Les séances déjà passées restent modifiables : un cours se modifie tant
    // qu'il n'est ni terminé ni transmis à l'URSSAF (règle portée par le menu
    // d'actions et par le serveur), aucune ligne n'est donc verrouillée.
    return apiSessions.map((session, index) => {
        const original = moment(session.date);
        const start = monthOffset > 0 ? shiftDraftSessionDate(session.date, monthOffset) : original;
        return {
            key: `session-api-${session.id ?? index}-${monthOffset}`,
            date: start ? start.format("YYYY-MM-DD") : null,
            startTime: (start ?? original).toDate().toISOString(),
            duration: (session.durationMinutes ?? 60) / 60,
        };
    });
};

/**
 * Ligne proposée par « Ajouter une séance » : la séance complète la plus
 * tardive, une semaine plus tard, même heure et même durée (élèves suivis de
 * façon hebdomadaire). Si la semaine suivante sort du mois de cette séance, la
 * date reste à choisir mais l'heure et la durée sont conservées. Sans séance
 * complète, ligne vide classique.
 */
export const nextSessionRow = (rows: SessionRow[]): SessionRow => {
    const complete = sortSessionRows(rows).filter((row) => sessionStart(row) !== null);
    const last = complete[complete.length - 1];
    if (!last) {
        return createEmptySessionRow();
    }
    const nextStart = sessionStart(last)!.add(1, "week");
    const sameMonth = nextStart.isSame(sessionStart(last)!, "month");
    return {
        ...createEmptySessionRow(),
        date: sameMonth ? nextStart.format("YYYY-MM-DD") : null,
        startTime: nextStart.toDate().toISOString(),
        duration: last.duration,
    };
};

/** Lignes complètes triées par date de début (les lignes incomplètes restent à la fin, dans l'ordre de saisie). */
export const sortSessionRows = (rows: SessionRow[]): SessionRow[] =>
    [...rows].sort((a, b) => {
        const startA = sessionStart(a);
        const startB = sessionStart(b);
        if (!startA || !startB) return (startA ? 0 : 1) - (startB ? 0 : 1);
        return startA.valueOf() - startB.valueOf();
    });

/** Payload API : [{date: ISO début de séance, durationMinutes}], dans l'ordre chronologique. */
export const sessionsToApiPayload = (rows: SessionRow[]) =>
    sortSessionRows(rows)
        .map((row) => {
            const start = sessionStart(row);
            return start
                ? { date: start.toDate().toISOString(), durationMinutes: Math.round(row.duration * 60) }
                : null;
        })
        .filter(Boolean) as { date: string; durationMinutes: number }[];

/**
 * Message d'erreur quand les séances se répartissent sur plusieurs mois : le
 * mois majoritaire est pris comme référence, les séances hors de ce mois sont
 * listées (numéro et date) pour que le prof sache lesquelles corriger.
 */
export const describeMonthMismatch = (rows: SessionRow[]): string => {
    const byMonth = new Map<string, number[]>();
    rows.forEach((row, index) => {
        const start = sessionStart(row);
        if (!start) return;
        const key = start.format("YYYY-MM");
        byMonth.set(key, [...(byMonth.get(key) ?? []), index]);
    });
    // mois de référence : le plus représenté, puis le plus ancien
    const [referenceKey] = [...byMonth.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))[0];
    const referenceLabel = capitalizeFirstLetter(moment(referenceKey, "YYYY-MM").locale("fr").format("MMMM YYYY"));
    const outside = rows
        .map((row, index) => ({ index, start: sessionStart(row) }))
        .filter(({ start }) => start && start.format("YYYY-MM") !== referenceKey)
        .map(({ index, start }) => `la séance n°${index + 1} (${start!.format("DD/MM/YYYY")})`);
    const list = outside.length > 1
        ? `${outside.slice(0, -1).join(", ")} et ${outside[outside.length - 1]}`
        : outside[0];
    return `Toutes les séances d'une même déclaration doivent avoir lieu dans le même mois calendaire. Les séances sont majoritairement en ${referenceLabel}, mais ${list} ${outside.length > 1 ? "sont" : "est"} sur un autre mois. Modifiez ${outside.length > 1 ? "ces séances" : "cette séance"} ou déclarez-${outside.length > 1 ? "les" : "la"} séparément.`;
};

/**
 * Valide les lignes de séances d'un cours mensuel :
 * au moins `minSessions` séances (2 par défaut), lignes complètes, durées
 * valides, même mois calendaire, max 8 séances.
 */
export const validateSessionRows = (
    rows: SessionRow[],
    options: { minSessions?: number } = {}
): { isValid: boolean; message: string } => {
    const label = getActivePrestationBrandConfig();
    const minSessions = options.minSessions ?? MIN_SESSIONS_MONTH;
    if (!rows || rows.length === 0) {
        return { isValid: false, message: `Vous devez détailler au moins une séance de ${label}.` };
    }
    if (rows.length < minSessions) {
        return {
            isValid: false,
            message: `Une déclaration « Plusieurs ${getActivePrestationBrandConfig("s")} » comporte au moins ${minSessions} séances. Pour une seule séance, déclarez plutôt ${getActivePrestationBrandConfig("", "un ", "une ")} ponctuel${label === "prestation" ? "le" : ""}.`,
        };
    }
    if (rows.length > MAX_SESSIONS_MONTH) {
        return { isValid: false, message: `Vous ne pouvez pas déclarer plus de ${MAX_SESSIONS_MONTH} séances sur un mois.` };
    }
    const incompleteIndex = rows.findIndex((row) => sessionStart(row) === null);
    if (incompleteIndex !== -1) {
        return {
            isValid: false,
            message: `La séance n°${incompleteIndex + 1} est incomplète : renseignez sa date et son heure de début.`,
        };
    }
    const invalidDurationIndex = rows.findIndex(
        (row) => !(row.duration > 0) || row.duration > MAX_SESSION_DURATION_HOURS
    );
    if (invalidDurationIndex !== -1) {
        return {
            isValid: false,
            message: `La durée de la séance n°${invalidDurationIndex + 1} doit être comprise entre 15 minutes et ${MAX_SESSION_DURATION_HOURS} heures.`,
        };
    }
    // Le calendrier démarre au mois en cours : aucune nouvelle séance ne peut
    // être positionnée sur un mois déjà passé (les séances verrouillées,
    // chargées depuis l'API, sont exemptées).
    const currentMonthStart = moment().startOf("month");
    const beforeMonthIndex = rows.findIndex(
        (row) => !row.locked && sessionStart(row)!.isBefore(currentMonthStart)
    );
    if (beforeMonthIndex !== -1) {
        return {
            isValid: false,
            message: `La séance n°${beforeMonthIndex + 1} ne peut pas être positionnée sur un mois déjà passé.`,
        };
    }
    const months = new Set(rows.map((row) => sessionStart(row)!.format("YYYY-MM")));
    if (months.size > 1) {
        return {
            isValid: false,
            message: describeMonthMismatch(rows),
        };
    }
    // Deux séances d'une même déclaration ne peuvent pas se chevaucher
    const overlap = findInternalOverlap(rows);
    if (overlap) {
        return { isValid: false, message: describeInternalOverlap(overlap) };
    }
    return { isValid: true, message: "" };
};

interface Props {
    rows: SessionRow[];
    onChange: (rows: SessionRow[]) => void;
    /** Date de validation du compte d'avance immédiate (borne min des séances). */
    startAt?: any;
    setIsOver?: (isOver: boolean) => void;
    /** Nombre minimum de séances (2 par défaut, 1 pour un ancien cours à séance unique). */
    minSessions?: number;
    /**
     * Écran d'arrivée : le calendrier du mois quand aucune séance n'est encore
     * datée (nouvelle déclaration), la liste sinon (brouillon, modification,
     * retour sur l'étape). Forçable par l'appelant.
     */
    initialMode?: "calendar" | "list";
}

/**
 * P1 – Formulaire multi-séances de la déclaration mensuelle : chaque séance
 * porte sa date, son heure de début et sa durée, avec ajout/suppression
 * dynamique et total calculé.
 */
const SessionList = ({ rows, onChange, startAt, setIsOver, minSessions = MIN_SESSIONS_MONTH, initialMode }: Props) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const label = getActivePrestationBrandConfig();
    const now = moment();
    const canRemove = rows.length > minSessions;
    // Calendrier d'abord quand rien n'est encore daté, liste ensuite ; le
    // bouton « Choisir les dates » rouvre le calendrier avec les dates cochées.
    const [mode, setMode] = React.useState<"calendar" | "list">(
        () => initialMode ?? (rows.some((row) => row.date) ? "list" : "calendar")
    );

    // Le calendrier démarre au mois en cours (ou à la validation du compte
    // d'avance immédiate si elle est plus récente).
    const currentMonthStart = moment().startOf("month");
    const minSelectableDate = startAt && moment(startAt).isAfter(currentMonthStart)
        ? moment(startAt)
        : currentMonthStart;

    const refreshIsOver = (nextRows: SessionRow[]) => {
        if (setIsOver) {
            const ends = nextRows.map(sessionEnd).filter(Boolean);
            setIsOver(ends.length > 0 && ends.every((end) => end!.isBefore(now)));
        }
    };

    const updateRow = (index: number, patch: Partial<SessionRow>) => {
        if (rows[index]?.locked) {
            return;
        }
        const nextRows = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
        onChange(nextRows);
        refreshIsOver(nextRows);
    };

    const addRow = () => {
        if (rows.length < MAX_SESSIONS_MONTH) {
            onChange([...rows, nextSessionRow(rows)]);
        }
    };

    const removeRow = (index: number) => {
        if (rows[index]?.locked) {
            return;
        }
        if (rows.length > minSessions) {
            const nextRows = rows.filter((_, i) => i !== index);
            onChange(nextRows);
            refreshIsOver(nextRows);
        }
    };

    const totalHours = sessionsTotalHours(rows);

    if (mode === "calendar") {
        return (
            <SessionCalendar
                rows={rows}
                minDate={minSelectableDate.format("YYYY-MM-DD")}
                startAt={startAt}
                onGenerate={(nextRows: SessionRow[]) => {
                    onChange(nextRows);
                    refreshIsOver(nextRows);
                    setMode("list");
                }}
                onCancel={rows.some((row) => row.date) ? () => setMode("list") : undefined}
            />
        );
    }

    return (
        <div className="w-full space-y-4">
            {rows.map((row, index) => {
                const end = sessionEnd(row);
                const start = sessionStart(row);
                return (
                    <div
                        key={row.key}
                        className={`w-full rounded-lg border p-3 space-y-3 ${row.locked ? "border-gray-200 bg-gray-50" : "border-gray-300"}`}
                    >
                        <div className="flex items-center justify-between">
                            <BrandContent
                                tag="p"
                                children={<>
                                    {capitalizeFirstLetter(label)} n°{index + 1}
                                    {row.locked && <span className="font-normal italic"> - 🔒 déjà passée, non modifiable</span>}
                                </>}
                                type="description"
                                className={`font-bold ${row.locked ? "" : classes.primaryText}`}
                            />
                            {!row.locked && (
                                <IconButton
                                    size="small"
                                    aria-label={`Supprimer la séance ${index + 1}`}
                                    onClick={() => removeRow(index)}
                                    disabled={!canRemove}
                                >
                                    <DeleteOutlineRoundedIcon
                                        fontSize="small"
                                        sx={{ color: !canRemove ? undefined : brandConfig.colors.primary }}
                                    />
                                </IconButton>
                            )}
                        </div>
                        {row.locked ? (
                            <div className="lg:grid flex flex-col grid-cols-2 gap-3">
                                <FormInput
                                    label="Jour de la séance"
                                    value={start ? capitalizeFirstLetter(start.format("dddd DD/MM/YYYY")) : ""}
                                    readOnly={true}
                                />
                                <FormInput
                                    label="Heure de début"
                                    value={start ? start.format("HH:mm") : ""}
                                    readOnly={true}
                                />
                                <FormInput
                                    label="Durée de la séance"
                                    value={Functions.renderDuration(row.duration * 2)}
                                    readOnly={true}
                                />
                                <FormInput
                                    label="Heure de fin"
                                    value={end ? end.format("HH:mm") : ""}
                                    readOnly={true}
                                />
                            </div>
                        ) : (
                            <div className="lg:grid flex flex-col grid-cols-2 gap-3">
                                <FormDate
                                    defaultValue={row.date ? new Date(row.date) : ""}
                                    defaultCalendarMonth={dayjs()}
                                    callback={(newDate: any) => {
                                        const iso = newDate && dayjs.isDayjs(newDate)
                                            ? newDate.format("YYYY-MM-DD")
                                            : newDate
                                                ? moment(newDate).format("YYYY-MM-DD")
                                                : null;
                                        updateRow(index, { date: iso });
                                    }}
                                    label={`Jour de la séance`}
                                    minDate={minSelectableDate.format("YYYY-MM-DD")}
                                />
                                <TimeFields
                                    value={row.startTime ? new Date(row.startTime) : undefined}
                                    date={row.date ? moment(row.date) : moment()}
                                    setStartTime={(date: Date) => updateRow(index, { startTime: date.toISOString() })}
                                    setEndTime={() => undefined}
                                    duration={row.duration}
                                    startAt={startAt}
                                    label="Heure de début"
                                />
                                <DurationFields
                                    customDuration={""}
                                    value={Functions.renderDuration(row.duration * 2)}
                                    isPunctualMonth={false}
                                    duration={row.duration}
                                    setDuration={(updater: any) => {
                                        const nextDuration = typeof updater === "function" ? updater(row.duration) : updater;
                                        updateRow(index, { duration: nextDuration });
                                    }}
                                    setEndTime={() => undefined}
                                    startTime={row.startTime ? new Date(row.startTime) : undefined}
                                    label="Durée de la séance"
                                />
                                <FormInput
                                    label="Heure de fin"
                                    value={end ? end.format("HH:mm") : ""}
                                    readOnly={true}
                                />
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                    <ButtonField
                        label="Choisir les dates"
                        startIcon={<CalendarMonthRoundedIcon fontSize="small" />}
                        variant="outlined"
                        onClick={() => setMode("calendar")}
                    />
                    <ButtonField
                        label="Ajouter une séance"
                        startIcon={<AddCircleOutlineRoundedIcon fontSize="small" />}
                        variant="outlined"
                        onClick={addRow}
                        disabled={rows.length >= MAX_SESSIONS_MONTH}
                    />
                </div>
                <BrandContent
                    tag="p"
                    children={
                        <>
                            {rows.length} séance{rows.length > 1 ? "s" : ""} - total :{" "}
                            <strong>{totalHours > 0 ? Functions.renderDuration(totalHours * 2) : "0h"}</strong>
                        </>
                    }
                    type="description"
                    className={`font-semibold ${common.body}`}
                />
            </div>
            {rows.length < minSessions && (
                <BrandContent
                    tag="p"
                    children={<>Au moins {minSessions} séances pour une déclaration « Plusieurs {label}s ». Pour une seule séance, déclarez plutôt {getActivePrestationBrandConfig("", "un ", "une ")} ponctuel{getActivePrestationBrandConfig("", "", "le")}.</>}
                    type="description"
                    className={`italic ${common.body}`}
                />
            )}
            {rows.length >= MAX_SESSIONS_MONTH && (
                <BrandContent
                    tag="p"
                    children={<>Maximum {MAX_SESSIONS_MONTH} séances par déclaration mensuelle.</>}
                    type="description"
                    className={`italic ${common.body}`}
                />
            )}
        </div>
    );
};

export default SessionList;
